import { CAUSES, MAX_RANKED_CAUSES, resolveCauseId } from "./causes";

// Text retrieval only, not a psychometric score. These aliases help learners
// find an existing catalogue choice; only their confirmed rank is scored.
const ALIASES: Record<string, string[]> = {
  poverty: ["poverty", "financial inclusion", "homelessness", "basic needs"],
  foodsecurity: ["hunger", "food security", "nutrition", "malnutrition", "affordable food"],
  healthaccess: ["healthcare", "health care", "medical care", "hospitals", "disease", "pandemic"],
  wellbeing: ["mental health", "anxiety", "depression", "wellbeing", "well being", "stress"],
  educationaccess: ["education", "schools", "literacy", "learning access", "school dropout"],
  fairwork: ["fair wages", "workers rights", "unemployment", "good jobs", "child labour", "child labor"],
  equalrights: ["gender equality", "discrimination", "equal rights", "racism", "women rights"],
  accessibility: ["disability", "disabilities", "wheelchair", "accessibility", "inclusive design"],
  childrenelders: ["elderly", "elder care", "child protection", "child care", "older people"],
  community: ["loneliness", "belonging", "community", "social isolation"],
  climate: ["climate change", "global warming", "carbon emissions", "greenhouse"],
  cleanenergy: ["renewable energy", "solar power", "wind power", "clean energy", "electricity access"],
  cleanwater: ["drinking water", "clean water", "sanitation", "sewage", "water shortage"],
  oceans: ["oceans", "ocean", "marine life", "coral", "sea pollution"],
  conservation: ["wildlife", "biodiversity", "forests", "deforestation", "endangered species"],
  animalwelfare: ["animal welfare", "stray dogs", "animal cruelty", "animal rescue", "pets"],
  wastecircular: ["recycling", "plastic waste", "waste", "reuse", "circular economy"],
  citiesfuture: ["road safety", "traffic", "public transport", "housing", "safer streets", "potholes", "pedestrian"],
  peacejustice: ["justice", "legal aid", "peace", "corruption", "violence", "human rights"],
  disasterrelief: ["disaster", "floods", "earthquakes", "emergency response", "disaster relief"],
  techforall: ["responsible ai", "ai ethics", "digital inclusion", "technology access", "algorithm bias"],
  onlinesafety: ["cyberbullying", "cyber bullying", "online safety", "online privacy", "scams", "data privacy"],
  trustedinfo: ["misinformation", "fake news", "fact checking", "disinformation", "trusted information"],
  heritage: ["heritage", "local languages", "culture", "traditions", "endangered languages"],
};

const normalize = (text: string) => text.toLowerCase().normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();

export interface CauseSuggestion { id: string; label: string; matchedTerms: string[] }

export function suggestCause(text: string): CauseSuggestion | null {
  const input = ` ${normalize(text)} `;
  if (input.trim().length < 3) return null;
  const matches = CAUSES.map((cause) => {
    const terms = [...(ALIASES[cause.id] ?? []), cause.label];
    const matchedTerms = terms.filter((term) => input.includes(` ${normalize(term)} `));
    const relevance = matchedTerms.reduce((sum, term) => sum + normalize(term).split(" ").length, 0);
    return { id: cause.id, label: cause.label, matchedTerms, relevance };
  }).filter((match) => match.relevance > 0).sort((a, b) => b.relevance - a.relevance);
  const best = matches[0];
  // No arbitrary guess when the text has equally strong connections.
  if (!best || matches[1]?.relevance === best.relevance) return null;
  return { id: best.id, label: best.label, matchedTerms: best.matchedTerms };
}

export function confirmSuggestedCause(ranked: string[], id: string): string[] {
  const validId = resolveCauseId(id);
  if (!CAUSES.some((cause) => cause.id === validId)) return ranked;
  if (ranked.some((cause) => resolveCauseId(cause) === validId) || ranked.length >= MAX_RANKED_CAUSES) return ranked;
  return [...ranked, validId];
}